import { useState, useEffect } from "react"
import { Form } from "antd"
import { formatTimeOffPayload } from "../utils/scheduleTimeOffHelpers"

/**
 * Custom hook to manage Schedule Time Off form state and handlers
 * @param {Function} onSave - Handler for save action
 * @param {Function} onSaveAndAddNew - Handler for save and add new action
 * @param {Function} onCancel - Handler for cancel action
 * @returns {Object} Form instance, state, and handlers
 */
const useScheduleTimeOffForm = (onSave, onSaveAndAddNew, onCancel) => {
  const [form] = Form.useForm()
  const [isPartOfDay, setIsPartOfDay] = useState(false)
  const [showScheduledTimeOff, setShowScheduledTimeOff] = useState(false)

  // Watch form values to enable/disable save buttons
  const startDate = Form.useWatch("startDate", form)
  const endDate = Form.useWatch("endDate", form)

  const isSaveEnabled = startDate && endDate

  // Check if start date and end date are the same day
  const isSameDay = startDate && endDate && startDate.isSame(endDate, 'day')

  // Automatically uncheck "Part of Day" when dates are no longer the same
  useEffect(() => {
    if (!isSameDay && isPartOfDay) {
      setIsPartOfDay(false)
    }
  }, [isSameDay, isPartOfDay])

  const handleCancel = () => {
    form.resetFields()
    setIsPartOfDay(false)
    setShowScheduledTimeOff(false)
    onCancel()
  }

  const handleFinish = (values) => {
    const payload = formatTimeOffPayload(values, isPartOfDay)
    const result = onSave(payload)

    // If onSave returns a promise, handle success/failure
    if (result && typeof result.then === 'function') {
      result
        .then(() => {
          handleCancel()
        })
        .catch(() => {
          // Keep modal open on error
        })
    } else {
      // Fallback for non-promise handlers
      handleCancel()
    }
  }

  const handleSaveAndAddNew = () => {
    form.validateFields().then((values) => {
      const payload = formatTimeOffPayload(values, isPartOfDay)
      const result = onSaveAndAddNew(payload)

      // If onSaveAndAddNew returns a promise, handle success/failure
      if (result && typeof result.then === 'function') {
        result
          .then(() => {
            resetForm()
          })
          .catch(() => {
            // Keep form data on error
          })
      } else {
        // Fallback for non-promise handlers
        resetForm()
      }
    })
  }

  const resetForm = () => {
    form.resetFields()
    setIsPartOfDay(false)
  }

  return {
    form,
    isPartOfDay,
    setIsPartOfDay,
    showScheduledTimeOff,
    setShowScheduledTimeOff,
    isSaveEnabled,
    isSameDay,
    handleCancel,
    handleFinish,
    handleSaveAndAddNew,
    resetForm
  }
}

export default useScheduleTimeOffForm
